import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {AuthService} from "../../services/auth.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";

import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {LoginDto, User} from "../../models/User";
import {Subscription} from "rxjs";
import {TranslateService, TranslateModule} from "@ngx-translate/core";
import {NgClass} from "@angular/common";
import {SocialLoginButtonsComponent} from "../social-login-buttons/social-login-buttons.component";

@Component({
  selector: "app-login-form",
  templateUrl: "./login-form.component.html",
  styleUrls: ["./login-form.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    RouterLink,
    TranslateModule,
    SocialLoginButtonsComponent,
  ],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  activationEmailSent = false;
  loginForm: FormGroup;
  error!: CustomError;
  user!: User;
  private readonly userLanguage = navigator.language.startsWith("fr")
    ? "fr"
    : "en";
  public submitButtonText?: string;
  public showPassword = false;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.loginForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
    });
  }

  ngOnInit() {
    this.translate.defaultLang = this.userLanguage;
    this.translate.reloadLang(this.userLanguage);
    this.translate.use(this.userLanguage);

    this.setSubmitText("LOGIN");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("LOGIN");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitButtonText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      this.error = {
        error: {
          error: "FILL_ALL_FIELDS",
        },
      };
      return;
    }

    this.setSubmitText("LOGGING_IN");

    const user: LoginDto = {
      email: this.loginForm.value.email,
      password: this.loginForm.value.password,
    };

    const loginSubscription = this.authService.login(user).subscribe({
      next: () => {
        void this.router.navigateByUrl(this.getRedirectUrl());
      },
      error: (error: CustomError) => {
        this.setSubmitText("LOGIN");
        this.error = error;
        this.cdr.markForCheck();
      },
    });
    this.subscriptions.push(loginSubscription);
    return false;
  }

  private getRedirectUrl(): string {
    const redirectTo = this.route.snapshot.queryParamMap.get("redirectTo");

    if (
      !redirectTo ||
      !redirectTo.startsWith("/") ||
      redirectTo.startsWith("//")
    ) {
      return "/";
    }

    return redirectTo;
  }

  sendActivationEmail() {
    const activationEmailSubscription = this.authService
      .sendActivationEmail(this.loginForm.value.email)
      .subscribe({
        next: () => {
          this.activationEmailSent = true;
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          this.error = error;
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(activationEmailSubscription);
  }

  onKeyUp(event: KeyboardEvent, type: string): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "sendActivationEmail") {
        this.sendActivationEmail();
      } else if (type === "showPassword") {
        this.showPassword = !this.showPassword;
      }
    }
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
